import * as Sentry from '@sentry/react'
import type { ErrorEvent, EventHint } from '@sentry/react'
import { isAxiosError } from 'axios'

const REPORTABLE_CLIENT_ERROR_STATUSES = [400, 404, 408]

const isReportableStatus = (status: number) =>
  REPORTABLE_CLIENT_ERROR_STATUSES.includes(status) ||
  (status >= 409 && status < 500)

export const isReportableError = (error: unknown) => {
  if (!isAxiosError(error)) return true

  return (
    error.response !== undefined && isReportableStatus(error.response.status)
  )
}

export const beforeSend = (event: ErrorEvent, hint: EventHint) =>
  isReportableError(hint.originalException) ? event : null

export const normalizeApiRoute = (url: string) =>
  url
    .split('?')[0]
    .replace(
      /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
      ':uuid',
    )
    .replace(/\d{4}-\d{2}-\d{2}/g, ':date')
    .replace(/\/\d+(?=\/|$)/g, '/:id')

export const reportBadResponse = (
  error: unknown,
  injected = { captureException: Sentry.captureException },
) => {
  if (!isAxiosError(error) || !error.response) return
  if (!isReportableError(error)) return

  const method = error.config?.method?.toUpperCase() ?? 'UNKNOWN'
  const route = normalizeApiRoute(error.config?.url ?? '')
  const status = String(error.response.status)

  injected.captureException(error, {
    fingerprint: ['api-error', method, route, status],
    tags: { 'api.method': method, 'api.route': route, 'api.status': status },
  })
}
